import {
  captureSnapshot,
  captureTrackingEvent,
  getEarlyEventQueue,
  type CaptureResult,
} from './earlyEvents';
import logger from '@/utils/client-logger';
import { getEventAdapter } from './adapters/eventAdapters';
import { ProviderRegistry } from './providerRegistry';
import { Provider, ProviderType, TrackingEvent } from './types';
import { generateEventId } from './utils/eventIdGenerator';

export class ReportingCenter {
  private static instance: ReportingCenter;

  static getInstance(): ReportingCenter {
    if (typeof window === 'undefined') return new ReportingCenter();
    if (!ReportingCenter.instance) {
      ReportingCenter.instance = new ReportingCenter();
      // 暴露日志控制方法到window对象
      if (typeof window !== 'undefined') {
        (window as any).enableReportCenterLog = ReportingCenter.instance.enableLogging.bind(
          ReportingCenter.instance,
        );
        (window as any).disableReportCenterLog = ReportingCenter.instance.disableLogging.bind(
          ReportingCenter.instance,
        );
        (window as any).getReportCenterLogStatus = ReportingCenter.instance.getLoggingStatus.bind(
          ReportingCenter.instance,
        );
        (window as any).listReportCenterProviders = ReportingCenter.instance.listProviders.bind(
          ReportingCenter.instance,
        );
      }
    }
    return ReportingCenter.instance;
  }

  private registry: ProviderRegistry;
  private initialized = false;
  private constructor() {
    this.registry = new ProviderRegistry();
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;

    const providers = this.registry.getEnabledProviders();
    const initPromises = providers.map(async (provider) => {
      try {
        await provider.init();
      } finally {
        await getEarlyEventQueue()?.drain();
      }
    });
    await Promise.allSettled(initPromises);
    this.initialized = true;
  }

  /** 注册时就发布独立派发能力，广告未ready不阻止ZV。 */
  registerProvider(provider: Provider): void {
    this.registry.registerProvider(provider);
    getEarlyEventQueue()?.register(provider.type, {
      enabled: provider.isEnabled,
      ready: () => provider.isInitialized(),
      ...(provider.getIdentity ? { identity: () => provider.getIdentity!() } : {}),
      ...(provider.applyIdentity
        ? { applyIdentity: (command) => provider.applyIdentity!(command) }
        : {}),
      dispatch: (input, event) => {
        if (provider.trackCaptured) return provider.trackCaptured(input);
        provider.track(
          event ?? {
            eventName: input.event.kind === 'track' ? input.event.eventName : input.event.kind,
            properties: 'properties' in input.event ? input.event.properties : {},
            eventId: input.logicalEventId,
          },
        );
        return { accepted: true };
      },
    });
  }

  /**
   * 追踪事件
   * @param event 事件数据 (可以是原始 TrackingEvent 或业务数据)
   * @param specificProviders 指定的 Provider 类型数组
   * @param adapterType 适配器类型，用于触发对应适配器 (如 'checkout.initiate')
   */
  track(
    event: TrackingEvent,
    specificProviders?: ProviderType[],
    adaptionData?: unknown,
    adapterType?: string,
  ): CaptureResult {
    if (typeof window === 'undefined') return { accepted: false, reason: 'server' };
    const snapshot = captureSnapshot();
    const providers = this.resolveTargetProviders(specificProviders);
    if (!providers.length && this.registry.getAllProviders().length)
      return { accepted: false, reason: 'disabled' };
    // Provider 尚未注册时仍接收明确目标，缺省只包含此次ZV早期入口。
    const targetTypes = providers.length
      ? providers.map((provider) => provider.type)
      : specificProviders?.length
        ? [...specificProviders]
        : Object.values(ProviderType);
    if (
      targetTypes.includes(ProviderType.SENSORS) &&
      !targetTypes.includes(ProviderType.SNOWPLOW) &&
      this.registry.getProvider(ProviderType.SNOWPLOW)?.isEnabled !== false
    )
      targetTypes.push(ProviderType.SNOWPLOW);
    const eventId =
      event.eventId ??
      (adapterType || targetTypes.includes(ProviderType.SENSORS) ? generateEventId() : undefined);
    let accepted = false;
    if (adapterType) {
      const adapter = getEventAdapter(adapterType);
      if (!adapter) return { accepted: false, reason: 'adapter_missing' };
      // 适配器在业务发生时执行；禁止ready后再读取当前Store/参数。
      for (const type of targetTypes) {
        try {
          const adapted = adapter(adaptionData, type, eventId);
          for (const item of adapted ? (Array.isArray(adapted) ? adapted : [adapted]) : [])
            accepted = captureTrackingEvent(item, [type], snapshot).accepted || accepted;
        } catch {
          logger.error('[ReportingCenter] adaptation_failed');
        }
      }
    } else {
      // 同次ZV事件共享一个logical ID和快照；广告事件保留既有独立eventId语义。
      const analytics = targetTypes.filter(
        (type) => type === ProviderType.SENSORS || type === ProviderType.SNOWPLOW,
      );
      if (analytics.length)
        accepted = captureTrackingEvent({ ...event, eventId }, analytics, snapshot).accepted;
      const advertisements = targetTypes.filter((type) => !analytics.includes(type));
      if (advertisements.length)
        accepted = captureTrackingEvent(event, advertisements, snapshot).accepted || accepted;
    }
    void getEarlyEventQueue()?.drain();
    return { accepted, ...(accepted ? {} : { reason: 'disabled_or_invalid' }) };
  }

  /**
   * 启用指定Provider的日志
   * @param providerTypes Provider类型数组，为空数组或undefined时启用所有Provider的日志
   */
  enableLogging(providerTypes?: string[]): void {
    const providers = this.getTargetProviders(providerTypes);

    providers.forEach((provider) => {
      if ('enableLog' in provider && typeof provider.enableLog === 'function') {
        (provider as any).enableLog();
        logger.log(`✅ Enabled logging for ${provider.type} provider`);
      } else {
        logger.warn(`⚠️ Provider ${provider.type} does not support logging control`);
      }
    });

    logger.log('📊 Current logging status:', this.getLoggingStatus());
  }

  /**
   * 禁用指定Provider的日志
   * @param providerTypes Provider类型数组，为空数组或undefined时禁用所有Provider的日志
   */
  disableLogging(providerTypes?: string[]): void {
    const providers = this.getTargetProviders(providerTypes);

    providers.forEach((provider) => {
      if ('disableLog' in provider && typeof provider.disableLog === 'function') {
        (provider as any).disableLog();
        logger.log(`❌ Disabled logging for ${provider.type} provider`);
      } else {
        logger.warn(`⚠️ Provider ${provider.type} does not support logging control`);
      }
    });

    logger.log('📊 Current logging status:', this.getLoggingStatus());
  }

  /**
   * 获取当前所有Provider的日志状态
   */
  getLoggingStatus(): Record<string, boolean | 'not_supported'> {
    const providers = this.registry.getAllProviders();
    const status: Record<string, boolean | 'not_supported'> = {};

    providers.forEach((provider) => {
      if ('loggingEnabled' in provider) {
        status[provider.type] = (provider as any).loggingEnabled;
      } else {
        status[provider.type] = 'not_supported';
      }
    });

    return status;
  }

  /**
   * 列出所有已注册的Provider
   */
  listProviders(): string[] {
    return this.registry.getAllProviders().map((provider) => provider.type);
  }

  /**
   * 获取特定类型的Provider实例
   */
  getProvider(type: ProviderType): Provider | undefined {
    return this.registry.getProvider(type);
  }

  private resolveTargetProviders(specificProviders?: ProviderType[]): Provider[] {
    let providers = this.registry.getEnabledProviders();

    if (specificProviders && specificProviders.length > 0) {
      providers = providers.filter((provider) => specificProviders.includes(provider.type));
    }

    return this.withSensorsSnowplowMirror(providers);
  }

  /**
   * 当目标包含 Sensors 时，自动纳入 Snowplow，并保证调用顺序为 Sensors -> Snowplow。
   */
  private withSensorsSnowplowMirror(providers: Provider[]): Provider[] {
    const hasSensors = providers.some((provider) => provider.type === ProviderType.SENSORS);
    const hasSnowplow = providers.some((provider) => provider.type === ProviderType.SNOWPLOW);

    let resolvedProviders = providers;
    if (hasSensors && !hasSnowplow) {
      const snowplowProvider = this.registry.getProvider(ProviderType.SNOWPLOW);
      if (snowplowProvider?.isEnabled) {
        resolvedProviders = [...providers, snowplowProvider];
      }
    }

    return this.orderSensorsSnowplowFirst(resolvedProviders);
  }

  private orderSensorsSnowplowFirst(providers: Provider[]): Provider[] {
    const sensorsProvider = providers.find((provider) => provider.type === ProviderType.SENSORS);
    if (!sensorsProvider) {
      return providers;
    }

    const snowplowProvider = providers.find((provider) => provider.type === ProviderType.SNOWPLOW);
    const remainingProviders = providers.filter(
      (provider) =>
        provider.type !== ProviderType.SENSORS && provider.type !== ProviderType.SNOWPLOW,
    );

    if (!snowplowProvider) {
      return [sensorsProvider, ...remainingProviders];
    }

    return [sensorsProvider, snowplowProvider, ...remainingProviders];
  }

  /**
   * 获取目标Provider列表
   */
  private getTargetProviders(providerTypes?: string[]): Provider[] {
    const allProviders = this.registry.getAllProviders();

    // 如果没有指定类型或传入空数组，返回所有Provider
    if (!providerTypes || providerTypes.length === 0) {
      return allProviders;
    }

    // 过滤指定类型的Provider
    return allProviders.filter((provider) => providerTypes.includes(provider.type));
  }
}
